import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import {
  getBaseCarcCode,
  getCarcLabelByCode,
  getFrequentlyUsedCarcCodes,
  getFrequentlyUsedNateraTagCodes,
  getFrequentlyUsedRarcCodes,
  getMappedRarcOptions,
  getNateraTagLabelByCode,
  getNateraTagOptions,
  getRarcLabelByCode,
} from '~/entities/carc-rarc'
import {
  getCategoryMetadata,
  isClaimAppealAcknowledgement,
  isClaimAppealRejected,
  useSubcategories,
} from '~/entities/categories'
import {
  DOCUMENT_SERVER_STATUS,
  getDocumentStepUrl,
  isLastStep,
  isTaggingClassification,
  ProcessingAlert,
  useClassificationReviews,
  useCurrentDocument,
  useDocumentExtractedCarcRarcData,
  useDocumentProcessingPolling,
} from '~/entities/document'
import { useAuditTrailQueryParams } from '~/features/audit-trail'
import { type ClaimPrefix, ClaimPrefixSelector } from '~/features/claim-prefix-filter'
import { useFirstInteraction, useStepCompletion, useStepView } from '~/features/document-analytics'
import { PrefixSuggestionCard, useOptiReasoning } from '~/features/opti-reasoning'
import { closePdfTabOrRemind } from '~/features/pdf-tab-reminder'
import {
  useAutoAcceptPatientMatchIndex,
  useSubmitPatientMatchingReview,
} from '~/pages/document-patient-matching/@x/document-tagging'
import { apiClient } from '~/shared/api/data-ingestion-api'
import { cn } from '~/shared/lib/cn'
import { useIsFeatureEnabled } from '~/shared/lib/feature-flags'
import { useIsScrolled } from '~/shared/lib/use-is-scrolled'
import { MultiSelect, NateraIcon, Text } from '~/shared/ui'
import { ReviewActions } from '~/widgets/review-actions'
import { useAddTagsReview, useSubmitTagsNotes } from '../../api'
import { CarcRarcGroup as CarcRarcGroupComponent } from './sections/carc-rarc-group'
import { TaggingComments } from './sections/tagging-comments'
import { TaggingHeader } from './sections/tagging-header'
import type { CarcRarcGroup } from './types'

const FREQUENTLY_USED_CODES_LIMIT = 5
const CLAIM_APPEAL_REJECTED_FREQUENT_RARCS = ['N255', 'N433']
const CLAIM_APPEAL_ACK_ALLOWED_TAGS = [
  'ATAT30',
  'ATAT45',
  'ATAT60',
  'ATAT90',
  'ATAT120',
  'ATAT180',
  'ATAT365',
  'TFNTSP',
]

let groupIdCounter = 0
function generateGroupId() {
  return `group-${++groupIdCounter}`
}

export function TaggingRedesign() {
  useStepView()
  const formRef = useRef<HTMLFormElement>(null)
  useFirstInteraction(formRef)
  const { trackStepCompletion } = useStepCompletion()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const {
    data: { document },
  } = useCurrentDocument()
  useDocumentProcessingPolling()
  const subcategories = useSubcategories()
  const { data: extractedCarcRarcData } = useDocumentExtractedCarcRarcData({
    documentKey: document.key,
  })
  const queryParams = useAuditTrailQueryParams()
  const { mutateAsync: addTagsReview, isError: isTagsReviewError } = useAddTagsReview()
  const { submitTagsNotes, isPending: isSubmitNotesPending } = useSubmitTagsNotes({
    documentKey: document.key,
    dataKey: extractedCarcRarcData?.key,
  })
  const isReviewStateResetEnabled = useIsFeatureEnabled('2026-07-review-state-reset')
  const {
    data: { items: classificationReviews },
  } = useClassificationReviews({ documentKey: document.key })
  const isFullyReviewed =
    document.review_status === DOCUMENT_SERVER_STATUS.reviewed ||
    document.review_status === DOCUMENT_SERVER_STATUS.auto_reviewed
  const wasReclassified =
    isReviewStateResetEnabled && (classificationReviews?.length ?? 0) >= 2 && !isFullyReviewed
  const { isScrolled, sentinelElement } = useIsScrolled()

  const isRestrictedTagsEnabled = useIsFeatureEnabled('2026-06-claim-appeal-restricted-tags')
  const isHideLlmRarcCarcEnabled = useIsFeatureEnabled('2026-07-hide-llm-rarc-carc')

  // PBDP-1412: when the patient-matching step already has a deterministic match,
  // completing the tagging step auto-completes patient matching too and returns
  // the reviewer to the list instead of advancing into the matching step.
  const isSkipDeterministicMatchingEnabled = useIsFeatureEnabled(
    '2026-07-skip-deterministic-patient-matching',
  )
  const isTaggingStep = isTaggingClassification(document.category, subcategories)
  const autoAcceptPatientMatchIndex = useAutoAcceptPatientMatchIndex({
    documentKey: document.key,
    enabled: isSkipDeterministicMatchingEnabled && isTaggingStep,
  })
  const shouldSkipPatientMatching =
    isSkipDeterministicMatchingEnabled && autoAcceptPatientMatchIndex !== null
  const { submitPatientMatchingReview } = useSubmitPatientMatchingReview({
    documentKey: document.key,
  })
  // Patient matching is the last step of the claim/tagging flow, so skipping it
  // means the tagging Validate becomes the final validation — fire the same PDF
  // tab-close reminder the patient step would have fired.
  const shouldRemindAfterSkip = isLastStep('patient', {
    isTaggingEnabled: isTaggingStep,
    isEob: false,
    isNonFinancialEob: false,
  })

  const hasTaggingBeenReviewed =
    document.reviewed_step === 'carc_rarc' ||
    document.reviewed_step === 'patient' ||
    document.review_status === 'reviewed' ||
    document.review_status === 'auto_reviewed'
  const shouldHideLlmSuggestions =
    isHideLlmRarcCarcEnabled && isTaggingClassification(document.category, subcategories)
  const shouldHideLlmRarcCarc = shouldHideLlmSuggestions && !hasTaggingBeenReviewed

  const categoryMetadata = useMemo(
    () => getCategoryMetadata({ category: document.category, subcategories }),
    [document.category, subcategories],
  )

  const serverCarcValues = useMemo(() => {
    const carcCodes = extractedCarcRarcData?.fields.find((field) => field.name === 'carc_codes')
      ?.value as apiClient.CarcRarcCode[] | undefined
    return carcCodes?.map((code) => getBaseCarcCode(code.code))
  }, [extractedCarcRarcData?.fields])

  const serverRarcValues = useMemo(() => {
    const rarcCodes = extractedCarcRarcData?.fields.find((field) => field.name === 'rarc_codes')
      ?.value as apiClient.CarcRarcCode[] | undefined
    return rarcCodes?.map((code) => code.code)
  }, [extractedCarcRarcData?.fields])

  const recommendedCarcValues = useMemo(() => {
    const recommended = extractedCarcRarcData?.original_fields_from_llm?.find(
      (field) => field.name === 'carc_codes',
    )?.value as apiClient.CarcRarcCode[] | undefined
    return recommended?.map((code) => getBaseCarcCode(code.code)) ?? []
  }, [extractedCarcRarcData?.original_fields_from_llm])

  const recommendedRarcValues = useMemo(() => {
    const recommended = extractedCarcRarcData?.original_fields_from_llm?.find(
      (field) => field.name === 'rarc_codes',
    )?.value as apiClient.CarcRarcCode[] | undefined
    return recommended?.map((code) => code.code) ?? []
  }, [extractedCarcRarcData?.original_fields_from_llm])

  const frequentCarcValues = useMemo(
    () =>
      getFrequentlyUsedCarcCodes(categoryMetadata)
        .map(getBaseCarcCode)
        .filter((v, i, arr) => arr.indexOf(v) === i)
        .slice(0, FREQUENTLY_USED_CODES_LIMIT),
    [categoryMetadata],
  )

  const frequentRarcValues = useMemo(() => {
    if (isClaimAppealRejected(categoryMetadata)) {
      return CLAIM_APPEAL_REJECTED_FREQUENT_RARCS
    }
    return getFrequentlyUsedRarcCodes(categoryMetadata).slice(0, FREQUENTLY_USED_CODES_LIMIT)
  }, [categoryMetadata])

  const serverNateraTagValues = useMemo(() => {
    const nateraTags = extractedCarcRarcData?.fields.find((field) => field.name === 'natera_tags')
      ?.value as apiClient.TagInfo[] | undefined
    return nateraTags?.map((tag) => tag.code)
  }, [extractedCarcRarcData?.fields])

  const recommendedNateraTagValues = useMemo(() => {
    const recommended = extractedCarcRarcData?.original_fields_from_llm?.find(
      (field) => field.name === 'natera_tags',
    )?.value as apiClient.TagInfo[] | undefined
    return recommended?.map((tag) => tag.code) ?? []
  }, [extractedCarcRarcData?.original_fields_from_llm])

  const resolvedNateraTagValues = useMemo(() => {
    if (shouldHideLlmSuggestions && !hasTaggingBeenReviewed) return undefined
    return serverNateraTagValues
  }, [shouldHideLlmSuggestions, hasTaggingBeenReviewed, serverNateraTagValues])

  const frequentNateraTagValues = useMemo(
    () => getFrequentlyUsedNateraTagCodes(categoryMetadata).slice(0, FREQUENTLY_USED_CODES_LIMIT),
    [categoryMetadata],
  )

  const nateraTagOptions = useMemo(
    () => getNateraTagOptions(categoryMetadata, serverNateraTagValues),
    [categoryMetadata, serverNateraTagValues],
  )

  const filteredNateraTagOptions = useMemo(() => {
    if (isRestrictedTagsEnabled && isClaimAppealAcknowledgement(document.category)) {
      const allowedOptions = nateraTagOptions.filter((opt) =>
        CLAIM_APPEAL_ACK_ALLOWED_TAGS.includes(opt.value),
      )
      const serverOnlyOptions = nateraTagOptions
        .filter(
          (opt) =>
            !CLAIM_APPEAL_ACK_ALLOWED_TAGS.includes(opt.value) &&
            serverNateraTagValues?.includes(opt.value),
        )
        .map((opt) => ({ ...opt, hidden_from_search: true }))
      return [...allowedOptions, ...serverOnlyOptions]
    }
    return nateraTagOptions
  }, [nateraTagOptions, isRestrictedTagsEnabled, document.category, serverNateraTagValues])

  const reasoningData = useOptiReasoning({
    llmFields: extractedCarcRarcData?.original_fields_from_llm,
  })

  const initialGroups = useMemo<CarcRarcGroup[]>(() => {
    if (shouldHideLlmRarcCarc) {
      return [{ id: generateGroupId(), carc_code: '', rarc_code: '', isUserAdded: false }]
    }
    if (isHideLlmRarcCarcEnabled && hasTaggingBeenReviewed && serverCarcValues?.length) {
      return serverCarcValues.map((code, i) => ({
        id: generateGroupId(),
        carc_code: code,
        rarc_code: serverRarcValues?.[i] ?? '',
        isUserAdded: false,
      }))
    }
    if (!recommendedCarcValues.length) {
      return [{ id: generateGroupId(), carc_code: '', rarc_code: '', isUserAdded: false }]
    }
    return recommendedCarcValues.map((code, i) => {
      const mapped = getMappedRarcOptions(code)
      let rarcCode = recommendedRarcValues[i] ?? ''

      if (rarcCode) {
        if (mapped && mapped.options.length > 0) {
          const isValidForThisCarc = mapped.options.some((o) => o.value === rarcCode)
          if (!isValidForThisCarc) {
            rarcCode = ''
          }
        } else if (mapped && mapped.options.length === 0) {
          rarcCode = ''
        }
      }

      if (!rarcCode && mapped && mapped.options.length === 1) {
        rarcCode = mapped.options[0].value
      }

      return { id: generateGroupId(), carc_code: code, rarc_code: rarcCode, isUserAdded: false }
    })
  }, [
    shouldHideLlmRarcCarc,
    isHideLlmRarcCarcEnabled,
    hasTaggingBeenReviewed,
    serverCarcValues,
    serverRarcValues,
    recommendedCarcValues,
    recommendedRarcValues,
  ])

  const initialPrefix = useMemo<ClaimPrefix | ''>(() => {
    if (shouldHideLlmRarcCarc) return ''
    if (isHideLlmRarcCarcEnabled && hasTaggingBeenReviewed && serverCarcValues?.length) {
      const firstCode = serverCarcValues[0] ?? ''
      if (firstCode.startsWith('DN')) return 'DN'
      if (firstCode.startsWith('RR')) return 'RR'
      return ''
    }
    const recommended = extractedCarcRarcData?.original_fields_from_llm?.find(
      (field) => field.name === 'carc_codes',
    )?.value as apiClient.CarcRarcCode[] | undefined
    const firstCode = recommended?.[0]?.code ?? ''
    if (firstCode.startsWith('DN')) return 'DN'
    if (firstCode.startsWith('RR')) return 'RR'
    return ''
  }, [
    shouldHideLlmRarcCarc,
    isHideLlmRarcCarcEnabled,
    hasTaggingBeenReviewed,
    serverCarcValues,
    extractedCarcRarcData?.original_fields_from_llm,
  ])

  const [claimPrefix, setClaimPrefix] = useState<ClaimPrefix | ''>(initialPrefix)

  const [groups, setGroups] = useState<CarcRarcGroup[]>(initialGroups)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const initialNateraTags = resolvedNateraTagValues ?? []
  const [nateraTags, setNateraTags] = useState<string[]>(initialNateraTags)

  const initialGroupsRef = useRef(initialGroups)
  const initialNateraTagsRef = useRef(initialNateraTags)
  if (
    initialGroupsRef.current !== initialGroups ||
    initialNateraTagsRef.current !== initialNateraTags
  ) {
    initialGroupsRef.current = initialGroups
    initialNateraTagsRef.current = initialNateraTags
    setGroups(initialGroups)
    setClaimPrefix(initialPrefix)
    setNateraTags(initialNateraTags)
  }

  const isPrefixChanged =
    initialPrefix !== '' && claimPrefix !== '' && claimPrefix !== initialPrefix

  const isGroupChanged = useCallback(
    (group: CarcRarcGroup, index: number) => {
      if (group.isUserAdded) return false
      const initial = initialGroups[index]
      if (!initial) return true
      if (group.carc_code !== initial.carc_code) return true
      if (!initial.rarc_code && group.rarc_code) return false
      return group.rarc_code !== initial.rarc_code
    },
    [initialGroups],
  )

  const hasSubstantiveChanges = useMemo(() => {
    if (isPrefixChanged) return true
    if (groups.some((g) => g.isUserAdded && g.carc_code)) return true
    return groups.some((g, i) => {
      if (g.isUserAdded) return false
      const initial = initialGroups[i]
      if (!initial) return true
      return g.carc_code !== initial.carc_code || g.rarc_code !== initial.rarc_code
    })
  }, [isPrefixChanged, groups, initialGroups])

  const matchesServerValues = useMemo(() => {
    if (!isHideLlmRarcCarcEnabled || !serverCarcValues?.length) return false
    const currentCarcCodes = groups.map((g) => g.carc_code).filter(Boolean)
    const currentRarcCodes = groups.map((g) => g.rarc_code).filter(Boolean)
    const serverRarcs = (serverRarcValues ?? []).filter(Boolean)
    return (
      currentCarcCodes.length === serverCarcValues.length &&
      currentCarcCodes.every((code, i) => code === serverCarcValues[i]) &&
      currentRarcCodes.length === serverRarcs.length &&
      currentRarcCodes.every((code, i) => code === serverRarcs[i])
    )
  }, [isHideLlmRarcCarcEnabled, groups, serverCarcValues, serverRarcValues])

  const handlePrefixChange = useCallback((prefix: ClaimPrefix) => {
    setClaimPrefix(prefix)
  }, [])

  const handleCarcChange = useCallback((groupId: string, value: string) => {
    setGroups((prev) =>
      prev.map((g) => {
        if (g.id !== groupId) return g
        if (!value) return { ...g, carc_code: '', rarc_code: '' }

        if (!g.isUserAdded) {
          const mapped = getMappedRarcOptions(value)
          if (mapped) {
            if (mapped.options.length === 1) {
              return { ...g, carc_code: value, rarc_code: mapped.options[0].value }
            }
            if (g.rarc_code && !mapped.options.some((o) => o.value === g.rarc_code)) {
              return { ...g, carc_code: value, rarc_code: '' }
            }
          }
        }

        return { ...g, carc_code: value }
      }),
    )
  }, [])

  const handleRarcChange = useCallback((groupId: string, value: string) => {
    setGroups((prev) => prev.map((g) => (g.id === groupId ? { ...g, rarc_code: value } : g)))
  }, [])

  const handleUserEvidenceChange = useCallback((groupId: string, value: string) => {
    setGroups((prev) => prev.map((g) => (g.id === groupId ? { ...g, user_evidence: value } : g)))
  }, [])

  const handleAddGroup = useCallback(() => {
    setGroups((prev) => [
      ...prev,
      { id: generateGroupId(), carc_code: '', rarc_code: '', isUserAdded: true },
    ])
  }, [])

  const handleClearAll = useCallback(() => {
    setGroups([{ id: generateGroupId(), carc_code: '', rarc_code: '', isUserAdded: false }])
    setClaimPrefix('')
  }, [])

  const handleSubmit = useCallback(async () => {
    if (!extractedCarcRarcData?.key) return
    setIsSubmitting(true)

    try {
      const carcCodes = groups
        .map((g) => g.carc_code)
        .filter(Boolean)
        .map((code) => ({
          type: 'carc' as const,
          code: `${claimPrefix}${code}`,
          description: getCarcLabelByCode(code) ?? '',
        }))

      const rarcCodes = groups
        .map((g) => g.rarc_code)
        .filter(Boolean)
        .map((code) => ({
          type: 'rarc' as const,
          code,
          description: getRarcLabelByCode(code) ?? '',
        }))

      await addTagsReview({
        path: { key: document.key },
        body: {
          data_key: extractedCarcRarcData.key,
          is_valid: true,
          fields: {
            carc_codes: carcCodes,
            rarc_codes: rarcCodes,
            natera_tags: nateraTags.map((code) => ({
              type: 'natera_tags' as const,
              code,
              description: getNateraTagLabelByCode(code) ?? '',
            })),
          },
        },
      })

      queryClient.invalidateQueries(
        apiClient.getDocumentTagsDocumentsKeyTagsGetOptions({
          path: { key: document.key },
        }),
      )

      trackStepCompletion()

      // PBDP-1412: when the match is deterministic, auto-complete the (final)
      // patient-matching step here and return to the list. The tags review POST
      // above only advances `reviewed_step` to `carc_rarc`; this second POST is
      // what flips the document to `reviewed`. Both are awaited so a failure in
      // either surfaces the tagging error and leaves the doc recoverable.
      if (shouldSkipPatientMatching) {
        await submitPatientMatchingReview({
          match_status: 'validated',
          selected_match_index: autoAcceptPatientMatchIndex,
        })
        if (shouldRemindAfterSkip) {
          void closePdfTabOrRemind(document.key)
        }
        // `useSubmitPatientMatchingReview` navigates back to the list on success.
        return
      }

      navigate(getDocumentStepUrl(document.key, 'patient', { queryParams }))
    } finally {
      setIsSubmitting(false)
    }
  }, [
    groups,
    claimPrefix,
    nateraTags,
    extractedCarcRarcData,
    document.key,
    addTagsReview,
    queryClient,
    trackStepCompletion,
    navigate,
    queryParams,
    shouldSkipPatientMatching,
    submitPatientMatchingReview,
    autoAcceptPatientMatchIndex,
    shouldRemindAfterSkip,
  ])

  const hasAtLeastOneCarc = groups.some((g) => g.carc_code)
  const userAddedGroupsHaveEvidence = groups
    .filter((g) => g.isUserAdded)
    .every((g) => g.user_evidence?.trim())
  const allRequiredRarcsSelected = groups.every((g) => {
    if (g.isUserAdded || !g.carc_code) return true
    const mapped = getMappedRarcOptions(g.carc_code)
    if (!mapped || mapped.options.length === 0) return true
    return !!g.rarc_code
  })
  const isFormValid = hasAtLeastOneCarc && userAddedGroupsHaveEvidence && allRequiredRarcsSelected

  return (
    <form
      ref={formRef}
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
      }}
    >
      {document.review_status === DOCUMENT_SERVER_STATUS.processing && (
        <ProcessingAlert className="mx-6 mt-4" />
      )}
      <div className="flex flex-col gap-6 px-6 py-4">
        <TaggingHeader
          groups={groups}
          prefix={claimPrefix}
          onClearAll={handleClearAll}
        />

        <div
          className={cn(
            'gap-6 rounded-lg border border-gray-200 p-5',
            shouldHideLlmSuggestions ? 'grid grid-cols-1' : 'grid grid-cols-2',
          )}
        >
          {!shouldHideLlmSuggestions && (
            <PrefixSuggestionCard
              prefix={reasoningData.prefixSuggestion.prefix}
              description={reasoningData.prefixSuggestion.description}
            />
          )}
          <ClaimPrefixSelector
            value={claimPrefix}
            optiSuggestedPrefix={
              shouldHideLlmSuggestions
                ? undefined
                : reasoningData.prefixSuggestion.prefix
                  ? (reasoningData.prefixSuggestion.prefix.slice(0, 2) as ClaimPrefix)
                  : undefined
            }
            onChange={handlePrefixChange}
          />
        </div>
        {isPrefixChanged && (
          <TaggingComments
            onSend={submitTagsNotes}
            isPending={isSubmitNotesPending}
          />
        )}

        {groups.map((group, index) => (
          <CarcRarcGroupComponent
            key={group.id}
            carcCode={group.carc_code}
            rarcCode={group.rarc_code}
            suggestion={
              shouldHideLlmSuggestions || group.isUserAdded
                ? undefined
                : reasoningData.carcSuggestions[index]
            }
            userEvidence={group.user_evidence}
            hasChanged={isGroupChanged(group, index)}
            isUserAdded={group.isUserAdded}
            isClaimAppealRejected={isClaimAppealRejected(categoryMetadata)}
            serverCarcValues={serverCarcValues}
            serverRarcValues={serverRarcValues}
            recommendedCarcValues={
              shouldHideLlmSuggestions || group.isUserAdded
                ? []
                : recommendedCarcValues[index]
                  ? [recommendedCarcValues[index]]
                  : []
            }
            recommendedRarcValues={
              shouldHideLlmSuggestions || group.isUserAdded
                ? []
                : initialGroups[index]?.rarc_code
                  ? [initialGroups[index].rarc_code]
                  : []
            }
            frequentCarcValues={frequentCarcValues}
            frequentRarcValues={frequentRarcValues}
            onSendComment={submitTagsNotes}
            isCommentPending={isSubmitNotesPending}
            onCarcChange={(value) => handleCarcChange(group.id, value)}
            onRarcChange={(value) => handleRarcChange(group.id, value)}
            onUserEvidenceChange={(value) => handleUserEvidenceChange(group.id, value)}
          />
        ))}

        <button
          type="button"
          className="text-primary-main w-full cursor-pointer rounded border-none bg-[#E0ECF2] py-3 text-center text-sm font-semibold"
          onClick={handleAddGroup}
        >
          + ADD CARC ↔ RARC
        </button>

        <div>
          <div className="flex items-center gap-3">
            <span className="bg-surface-medium flex size-6 items-center justify-center rounded-full">
              <NateraIcon size="18" />
            </span>
            <Text appearance="SUBTITLE2">Natera Tags</Text>
          </div>
          <Text
            className="mt-1 mb-4 block"
            appearance="CAPTION"
          >
            Select options that are relevant to the content in the document to improve routing and
            downstream workflows.
          </Text>
          <MultiSelect
            label="Natera Tags"
            placeholder="Select Natera Tags"
            required={false}
            options={filteredNateraTagOptions}
            frequentValues={frequentNateraTagValues}
            frequentGroupLabel="Frequently Used Natera Tags"
            recommendedValues={shouldHideLlmRarcCarc ? [] : recommendedNateraTagValues}
            recommendedGroupLabel="opti Suggested Natera Tags"
            value={nateraTags}
            onValueChange={setNateraTags}
          />
        </div>
      </div>

      <ReviewActions
        isDirty={hasSubstantiveChanges}
        isSubmitDisabled={
          isSubmitting ||
          !isFormValid ||
          document.review_status === DOCUMENT_SERVER_STATUS.processing ||
          (!wasReclassified && hasTaggingBeenReviewed && !hasSubstantiveChanges) ||
          (!wasReclassified && hasTaggingBeenReviewed && matchesServerValues)
        }
        onSubmit={handleSubmit}
        submitLabel="Validate"
        submitTooltip={
          !hasAtLeastOneCarc
            ? 'Select at least one CARC code.'
            : !allRequiredRarcsSelected
              ? 'Select a RARC code for required fields.'
              : undefined
        }
        canUndo={false}
        canRedo={false}
        onRevert={handleClearAll}
        onUndo={() => {}}
        onRedo={() => {}}
        previousStepUrl={getDocumentStepUrl(document.key, 'data', { queryParams })}
        previousStepLabel="Go to data extraction"
        isError={isTagsReviewError}
        errorContent="opti ran into a problem while saving the tags. Please try again."
        isScrolled={isScrolled}
      />
      {sentinelElement}
    </form>
  )
}
